<template>
  <div>
    <div class="panel">
      <div class="flex justify-end mb-3">
        <v-button
          icon="copy"
          :disabled="isLoading || !flatTypes.length"
          @click="openCopyFromProjectModal"
        >{{ $t('copy_issue_type_users_from_project') }}</v-button>
      </div>
      <section>
        <b-table
          show-empty
          icon-pack="fas"
          :data="flatTypes"
          :loading="isLoading"
          striped
          bordered
          detailed
          detail-key="id"
          :opened-detailed="openedDetailedIds"
          @update:openedDetailed="openedDetailedIds = $event"
        >
          <template v-slot:empty="">
            <h4>{{ $t('no_rows') }}</h4>
          </template>

          <template>
            <b-table-column field="code" sortable width="200">
              <template v-slot:header="">
                {{ $t('code') }}
              </template>
              <template v-slot="props">
                {{ props.row.code }}
              </template>
            </b-table-column>

            <b-table-column field="name" sortable>
              <template v-slot:header="">
                {{ $t('name') }}
              </template>
              <template v-slot="props">
                {{ props.row.displayName || props.row.name }}
              </template>
            </b-table-column>

            <b-table-column field="description">
              <template v-slot:header="">
                {{ $t('description') }}
              </template>
              <template v-slot="props">
                {{ props.row.description }}
              </template>
            </b-table-column>

            <b-table-column field="actions" centered width="80">
              <template v-slot:header="">
                {{ $t('actions') }}
              </template>
              <template v-slot="props">
                <b-tooltip
                  position="is-left"
                  :label="$t('issue_type_participants')"
                >
                  <div
                    @click.stop="openParticipants(props.row)"
                    class="cursor-pointer flex text-blue justify-center"
                  >
                    <b-icon pack="fas" icon="user-plus" />
                  </div>
                </b-tooltip>
              </template>
            </b-table-column>
          </template>

          <template slot="detail" slot-scope="props">
            <div class="p-3" v-if="props.row && projectId">
              <div class="mb-3 flex flex-wrap items-center" style="gap: 1rem;">
                <v-button
                  icon="copy"
                  :disabled="props.row.use_project_issue_users || getIssueTypesForCopy(props.row.id).length === 0"
                  @click="openCopyUsersModal(props.row)"
                >{{ $t('copy_issue_type_users') }}</v-button>

                <b-field>
                  <b-checkbox
                    :value="!!props.row.use_project_issue_users"
                    :disabled="hasTypeUsers(props.row.id) || togglingTypeId === props.row.id"
                    @input="(checked) => toggleUseProjectIssueUsers(props.row, checked)"
                  >
                    {{ $t('use_project_issue_users') }}
                  </b-checkbox>
                </b-field>
              </div>

              <p
                v-if="props.row.use_project_issue_users"
                class="mb-3 has-text-grey"
              >
                {{ $t('use_project_issue_users_hint') }}
              </p>

              <userlist
                v-if="!props.row.use_project_issue_users"
                :key="'issue-type-users-' + props.row.id + '-' + projectId + '-' + getIssueTypeUsersListKey(props.row.id)"
                :title="'issue_type_participants'"
                :parent-entity-id="parseInt(props.row.id)"
                icon="users"
                api-url="/api/v2/issue-type"
                :users-permission-type="['participate']"
                :allow-create-users="false"
                :allow-zero-users="true"
                :only-internal-users="false"
                :project-id="projectId"
                roleName="issue_user"
                class="w-full md:w-1/2"
                @user-added="() => onTypeUsersChanged(props.row.id)"
              />
            </div>
          </template>
        </b-table>
      </section>
    </div>

    <b-modal :active.sync="copyUsersModalActive" :width="600" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('copy_issue_type_users') }}</p>
          <button class="delete" aria-label="close" @click="closeCopyUsersModal"></button>
        </header>
        <section class="modal-card-body">
          <p class="mb-4">{{ $t('copy_issue_type_users_confirm') }}</p>
          <b-field :label="$t('select_issue_type')">
            <b-select
              v-model="copyUsersSourceTypeId"
              expanded
              :placeholder="$t('select_issue_type')"
            >
              <option
                v-for="issueType in copyUsersSourceOptions"
                :key="'copy-source-' + issueType.id"
                :value="issueType.id"
              >
                {{ issueType.label }}
              </option>
            </b-select>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <b-button
            type="is-primary"
            :loading="copyUsersLoading"
            :disabled="!copyUsersSourceTypeId"
            @click="confirmCopyIssueTypeUsers"
          >
            {{ $t('copy_issue_type_users') }}
          </b-button>
          <b-button @click="closeCopyUsersModal">{{ $t('cancel') }}</b-button>
        </footer>
      </div>
    </b-modal>

    <b-modal :active.sync="copyFromProjectModalActive" :width="600" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('copy_issue_type_users_from_project') }}</p>
          <button class="delete" aria-label="close" @click="closeCopyFromProjectModal"></button>
        </header>
        <section class="modal-card-body">
          <p class="mb-4">{{ $t('copy_issue_type_users_from_project_confirm') }}</p>
          <b-field :label="$t('select_project')">
            <b-select
              v-model="copyFromProjectSourceId"
              expanded
              :loading="copyFromProjectOptionsLoading"
              :placeholder="$t('select_project')"
            >
              <option
                v-for="project in copyFromProjectOptions"
                :key="'copy-project-' + project.id"
                :value="project.id"
              >
                {{ project.name }}
              </option>
            </b-select>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <b-button
            type="is-primary"
            :loading="copyFromProjectLoading"
            :disabled="!copyFromProjectSourceId"
            @click="confirmCopyFromProject"
          >
            {{ $t('copy_issue_type_users_from_project') }}
          </b-button>
          <b-button @click="closeCopyFromProjectModal">{{ $t('cancel') }}</b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Userlist from "~/components/UserBox/UserList.vue";
import { mapGetters } from "vuex";

export default {
  name: "project-issue-type-users",

  components: {
    Userlist,
  },

  props: {
    projectId: { type: Number, required: true },
  },

  data: () => ({
    data: [],
    isLoading: false,
    openedDetailedIds: [],
    copyUsersModalActive: false,
    copyUsersTargetType: null,
    copyUsersSourceTypeId: null,
    copyUsersSourceOptions: [],
    copyUsersLoading: false,
    issueTypeUsersListKeys: {},
    typeUsersCounts: {},
    togglingTypeId: null,
    copyFromProjectModalActive: false,
    copyFromProjectSourceId: null,
    copyFromProjectOptions: [],
    copyFromProjectOptionsLoading: false,
    copyFromProjectLoading: false,
  }),

  computed: {
    ...mapGetters({
      company: "app/company",
    }),

    flatTypes() {
      const result = [];
      const projectId = Number(this.projectId);

      const visit = (item, parentLabel = null) => {
        if (!item || !item.id) {
          return;
        }

        result.push({
          ...item,
          displayName: parentLabel
            ? `${parentLabel} › ${item.name}`
            : item.name,
          use_project_issue_users: this.isUseProjectIssueUsersEnabled(item, projectId),
        });

        if (item.children && item.children.length) {
          item.children.forEach((child) => visit(child, item.name));
        }
      };

      this.data.forEach((item) => visit(item));

      return result;
    },
  },

  mounted() {
    this.loadIssueTypes();
  },

  watch: {
    openedDetailedIds(ids) {
      (ids || []).forEach((id) => this.refreshTypeUsersCount(id));
    },
  },

  methods: {
    async loadIssueTypes() {
      this.isLoading = true;

      try {
        const { data } = await axios.get(
          "/api/v2/company/" + this.company.id + "/issue-types",
          {
            params: {
              entity_type: "project",
              company_id: this.company.id,
            },
          }
        );

        if (data && data.success) {
          this.data = data.data || [];
        } else {
          this.$notify.error("error_loading_data");
        }
      } catch (error) {
        this.$notify.error("error_loading_data");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    openParticipants(row) {
      if (!row || !row.id) {
        return;
      }

      if (!this.openedDetailedIds.includes(row.id)) {
        this.openedDetailedIds = [...this.openedDetailedIds, row.id];
      }

      this.refreshTypeUsersCount(row.id);
    },

    hasTypeUsers(typeId) {
      return (this.typeUsersCounts[typeId] || 0) > 0;
    },

    isUseProjectIssueUsersEnabled(item, projectId = this.projectId) {
      const ids = (item && item.config && item.config.use_project_issue_users) || [];
      return ids.map(Number).includes(Number(projectId));
    },

    async refreshTypeUsersCount(typeId) {
      if (!typeId || !this.projectId) {
        return;
      }

      try {
        const { data } = await axios.get(`/api/v2/issue-type/${typeId}/users`, {
          params: { p: this.projectId },
        });

        this.$set(this.typeUsersCounts, typeId, (data.users_list || []).length);

        if (typeof data.use_project_issue_users !== "undefined") {
          this.setTypeFlag(typeId, !!data.use_project_issue_users);
        }
      } catch (error) {
        console.error(error);
      }
    },

    setTypeFlag(typeId, enabled) {
      const projectId = Number(this.projectId);

      const visit = (items) => {
        (items || []).forEach((item) => {
          if (item.id === typeId) {
            const config = Object.assign({}, item.config || {});
            let ids = ((config.use_project_issue_users || []).map(Number));

            if (enabled) {
              if (!ids.includes(projectId)) {
                ids.push(projectId);
              }
            } else {
              ids = ids.filter((id) => id !== projectId);
            }

            config.use_project_issue_users = ids;
            this.$set(item, "config", config);
            this.$set(item, "use_project_issue_users", enabled);
          }
          if (item.children && item.children.length) {
            visit(item.children);
          }
        });
      };

      visit(this.data);
    },

    onTypeUsersChanged(typeId) {
      this.refreshTypeUsersCount(typeId);
    },

    async toggleUseProjectIssueUsers(row, enabled) {
      if (!row || !row.id || !this.projectId) {
        return;
      }

      if (enabled && this.hasTypeUsers(row.id)) {
        this.$notify.error("issue_type_has_participants");
        return;
      }

      this.togglingTypeId = row.id;

      try {
        const { data } = await axios.post(
          `/api/v2/issue-type/${row.id}/use-project-issue-users`,
          {
            project_id: this.projectId,
            use_project_issue_users: !!enabled,
          }
        );

        if (data && data.success) {
          this.setTypeFlag(row.id, !!data.use_project_issue_users);
          this.$notify.success("success_editing");
        } else {
          this.$notify.error(data.msg || "error_saving");
        }
      } catch (error) {
        this.$notify.error("error_saving");
        console.error(error);
      } finally {
        this.togglingTypeId = null;
      }
    },

    getIssueTypesForCopy(excludeId) {
      return this.flatTypes
        .filter((item) => item.id !== excludeId)
        .map((item) => ({
          id: item.id,
          label: `${item.code} - ${item.name}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },

    getIssueTypeUsersListKey(typeId) {
      return this.issueTypeUsersListKeys[typeId] || 0;
    },

    openCopyUsersModal(row) {
      this.copyUsersTargetType = row;
      this.copyUsersSourceTypeId = null;
      this.copyUsersSourceOptions = this.getIssueTypesForCopy(row.id);
      this.copyUsersModalActive = true;
    },

    closeCopyUsersModal() {
      this.copyUsersModalActive = false;
      this.copyUsersTargetType = null;
      this.copyUsersSourceTypeId = null;
      this.copyUsersSourceOptions = [];
    },

    async confirmCopyIssueTypeUsers() {
      if (!this.copyUsersTargetType || !this.copyUsersSourceTypeId || !this.projectId) {
        return;
      }

      this.copyUsersLoading = true;

      try {
        const { data } = await axios.post(
          `/api/v2/issue-type/${this.copyUsersTargetType.id}/users/copy-from`,
          {
            source_issue_type_id: this.copyUsersSourceTypeId,
            project_id: this.projectId,
          }
        );

        if (data && data.success) {
          this.$notify.success("copy_issue_type_users_success");
          const typeId = this.copyUsersTargetType.id;
          this.setTypeFlag(typeId, false);
          this.$set(
            this.issueTypeUsersListKeys,
            typeId,
            (this.issueTypeUsersListKeys[typeId] || 0) + 1
          );
          this.refreshTypeUsersCount(typeId);
          this.closeCopyUsersModal();
        } else {
          this.$notify.error(data.msg || "error_saving");
        }
      } catch (error) {
        this.$notify.error("error_saving");
        console.error(error);
      } finally {
        this.copyUsersLoading = false;
      }
    },

    async openCopyFromProjectModal() {
      this.copyFromProjectSourceId = null;
      this.copyFromProjectModalActive = true;

      if (this.copyFromProjectOptions.length) {
        return;
      }

      this.copyFromProjectOptionsLoading = true;

      try {
        const { data } = await axios.get("/api/v2/user/projects", {
          params: { showArchive: 1, mappedForSelect: true, companyId: this.company.id },
        });

        this.copyFromProjectOptions = (data.projects || [])
          .filter((project) => Number(project.id) !== Number(this.projectId))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      } catch (error) {
        this.$notify.error("error_loading_data");
        console.error(error);
      } finally {
        this.copyFromProjectOptionsLoading = false;
      }
    },

    closeCopyFromProjectModal() {
      this.copyFromProjectModalActive = false;
      this.copyFromProjectSourceId = null;
    },

    async confirmCopyFromProject() {
      if (!this.copyFromProjectSourceId || !this.projectId) {
        return;
      }

      this.copyFromProjectLoading = true;

      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/issue-type-users/copy-from`,
          { source_project_id: this.copyFromProjectSourceId }
        );

        if (data && data.success) {
          this.$notify.success("copy_issue_type_users_from_project_success", {
            added: data.added || 0,
            skipped: data.skipped || 0,
          });

          // Recargar flags de config y forzar el refresco de las listas de usuarios abiertas
          await this.loadIssueTypes();
          this.typeUsersCounts = {};
          const keys = {};
          this.flatTypes.forEach((type) => {
            keys[type.id] = (this.issueTypeUsersListKeys[type.id] || 0) + 1;
          });
          this.issueTypeUsersListKeys = keys;
          this.openedDetailedIds.forEach((id) => this.refreshTypeUsersCount(id));

          this.closeCopyFromProjectModal();
        } else {
          this.$notify.error(data.msg || "error_saving");
        }
      } catch (error) {
        this.$notify.error("error_saving");
        console.error(error);
      } finally {
        this.copyFromProjectLoading = false;
      }
    },
  },
};
</script>
